import { useId, useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../lib/api.js";
import {
  createAccount,
  updateAccount,
} from "../../lib/api-modules/accounts.js";
import { listFamilyMembers } from "../../lib/api-modules/family-members.js";
import {
  ACCOUNT_TYPE_LABELS,
  CASH_ACCOUNT_TYPES,
  CRYPTO_ACCOUNT_TYPES,
  INVESTMENT_ACCOUNT_TYPES,
  LIABILITY_ACCOUNT_TYPES,
  type AccountDrilldownDetail,
  type AccountOwnership,
  type AccountSummary,
  type AccountType,
  type CostBasisMethod,
  type CreateAccountInput,
  type HsaMode,
  type UpdateAccountInput,
} from "../../lib/api-modules/types.js";
import { centsToDollars, dollarsToCents } from "../../lib/util/money.js";
import { ErrorMessage } from "../auth/error-message.js";

/**
 * <AccountForm> — single component, account-type-aware conditional
 * sections (Phase 3 Session 2).
 *
 * **Why one component, not three:** the always-rendered fields (name,
 * institution, ownership, notes) outweigh the conditional ones, and the
 * 3-4 per-type sections are small enough that splitting into
 * <InvestmentAccountForm>/<LiabilityAccountForm>/<CashAccountForm>
 * would duplicate more boilerplate than it would clarify. Phase 5's
 * discriminated-union refactor will revisit if the conditional logic
 * outgrows this shape.
 *
 * **Money inputs are dollars** (string), converted to integer cents at
 * submit time via `dollarsToCents`. Backend Zod rejects floats anyway.
 *
 * **Cross-source guard mirrors the backend:** if `account.source` is
 * non-manual (Merrill-imported), most fields are disabled (only name,
 * notes, ownership, beneficiary, cost_basis_method are editable). The
 * backend enforces the same restriction; the disable here is a UX hint.
 */

const OWNERSHIP_OPTIONS: ReadonlyArray<{ value: AccountOwnership; label: string }> =
  [
    { value: "primary", label: "Primary" },
    { value: "spouse", label: "Spouse" },
    { value: "joint", label: "Joint" },
    { value: "minor", label: "Minor" },
    { value: "trust", label: "Trust" },
  ];

const COST_BASIS_OPTIONS: ReadonlyArray<{ value: CostBasisMethod; label: string }> =
  [
    { value: "average", label: "Average cost (default)" },
    { value: "fifo", label: "FIFO (first in, first out)" },
    { value: "lifo", label: "LIFO (last in, first out)" },
    { value: "specific", label: "Specific identification" },
  ];

const HSA_MODE_OPTIONS: ReadonlyArray<{ value: HsaMode; label: string }> = [
  { value: "receipts", label: "Receipts vault (reimburse later)" },
  { value: "invested", label: "Invested (long-horizon)" },
];

const CHAIN_SUGGESTIONS: ReadonlyArray<string> = [
  "ethereum",
  "bitcoin",
  "solana",
  "polygon",
  "arbitrum",
  "base",
];

interface AccountFormProps {
  readonly mode: "create" | "edit";
  readonly account?: AccountSummary | AccountDrilldownDetail;
  readonly onClose: () => void;
}

/**
 * The two account shapes carry different field sets — `AccountSummary`
 * is the list-row roll-up, `AccountDrilldownDetail` is the per-account
 * full record. We accept either in edit mode and read whichever fields
 * are present.
 */
type EditableAccount = AccountSummary | AccountDrilldownDetail;

function readField<T>(account: EditableAccount | undefined, key: string): T | undefined {
  if (!account) return undefined;
  const value = (account as unknown as Record<string, unknown>)[key];
  return value === null || value === undefined ? undefined : (value as T);
}

export function AccountForm({ mode, account, onClose }: AccountFormProps) {
  const queryClient = useQueryClient();
  const isEdit = mode === "edit";
  const sourceLocked =
    isEdit &&
    account?.source !== undefined &&
    account.source !== null &&
    account.source !== "manual";

  // Always-shown fields
  const [name, setName] = useState(account?.name ?? "");
  const [institution, setInstitution] = useState(account?.institution ?? "");
  const [accountType, setAccountType] = useState<AccountType>(
    (account?.account_type ?? "taxable"),
  );
  const [ownership, setOwnership] = useState<AccountOwnership>(
    (readField<AccountOwnership>(account, "ownership") ?? "primary"),
  );
  const [notes, setNotes] = useState<string>(
    readField<string>(account, "notes") ?? "",
  );

  // Investment fields
  const [costBasisMethod, setCostBasisMethod] = useState<CostBasisMethod>(
    (account?.cost_basis_method ?? "average"),
  );

  // Cash fields (dollar strings — converted at submit)
  const [manualBalance, setManualBalance] = useState<string>(
    centsOrEmpty(readField<number>(account, "manual_balance_cents")),
  );
  const [manualBalanceAsOf, setManualBalanceAsOf] = useState<string>(
    readField<string>(account, "manual_balance_as_of") ?? "",
  );

  // Beneficiary (529/UTMA)
  const [beneficiaryMemberId, setBeneficiaryMemberId] = useState<string>(
    readField<string>(account, "beneficiary_member_id") ?? "",
  );

  // Phase 5 Session 5 — 529/UTMA plan-state + account-owner/custodian.
  const [planStateCode, setPlanStateCode] = useState<string>(
    readField<string>(account, "plan_state_code") ?? "",
  );
  const [accountOwnerMemberId, setAccountOwnerMemberId] = useState<string>(
    readField<string>(account, "account_owner_member_id") ?? "",
  );

  // Liability fields
  const [originatingPrincipal, setOriginatingPrincipal] = useState<string>(
    centsOrEmpty(readField<number>(account, "originating_principal_cents")),
  );
  const [currentPrincipal, setCurrentPrincipal] = useState<string>(
    centsOrEmpty(readField<number>(account, "current_principal_cents")),
  );
  const [interestRate, setInterestRate] = useState<string>(
    readField<number>(account, "interest_rate_pct")?.toString() ?? "",
  );
  const [payoffDate, setPayoffDate] = useState<string>(
    readField<string>(account, "payoff_date") ?? "",
  );
  const [minimumPayment, setMinimumPayment] = useState<string>(
    centsOrEmpty(readField<number>(account, "minimum_payment_cents")),
  );
  const [paymentDueDay, setPaymentDueDay] = useState<string>(
    readField<number>(account, "payment_due_day_of_month")?.toString() ?? "",
  );

  // Credit-card extras
  const [aprPct, setAprPct] = useState<string>(
    readField<number>(account, "apr_pct")?.toString() ?? "",
  );
  const [creditLimit, setCreditLimit] = useState<string>(
    centsOrEmpty(readField<number>(account, "credit_limit_cents")),
  );
  const [rewardsProgram, setRewardsProgram] = useState<string>(
    readField<string>(account, "rewards_program") ?? "",
  );
  const [annualFee, setAnnualFee] = useState<string>(
    centsOrEmpty(readField<number>(account, "annual_fee_cents")),
  );

  // HSA + crypto
  const [hsaMode, setHsaMode] = useState<HsaMode>(
    (readField<HsaMode>(account, "hsa_mode") ?? "receipts"),
  );
  const [chain, setChain] = useState<string>(
    readField<string>(account, "chain") ?? "",
  );
  const [walletAddress, setWalletAddress] = useState<string>(
    readField<string>(account, "wallet_address") ?? "",
  );

  const [inlineError, setInlineError] = useState<string | null>(null);

  const showInvestment = useMemo(
    () =>
      INVESTMENT_ACCOUNT_TYPES.has(accountType) ||
      (accountType === "hsa" && hsaMode === "invested"),
    [accountType, hsaMode],
  );
  const showCash = CASH_ACCOUNT_TYPES.has(accountType);
  const showLiability = LIABILITY_ACCOUNT_TYPES.has(accountType);
  const showCreditCardExtras = accountType === "credit_card";
  const showHsa = accountType === "hsa";
  const showBeneficiary =
    accountType === "529" ||
    accountType === "529_prepaid" ||
    accountType === "utma";
  const showCrypto = CRYPTO_ACCOUNT_TYPES.has(accountType);

  const mutation = useMutation({
    mutationFn: async () => {
      const input = buildPayload();
      if (isEdit && account) {
        return updateAccount(account.account_id, input);
      }
      return createAccount(input as CreateAccountInput);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["accounts"] });
      if (isEdit && account) {
        await queryClient.invalidateQueries({
          queryKey: ["accounts", account.account_id, "holdings"],
        });
      }
      onClose();
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : "Couldn't save. Try again.",
      );
    },
  });

  function buildPayload(): CreateAccountInput | UpdateAccountInput {
    const out: Record<string, unknown> = {
      name,
      institution,
      account_type: accountType,
      ownership,
    };
    if (notes.trim().length > 0) out["notes"] = notes.trim();
    if (showInvestment) out["cost_basis_method"] = costBasisMethod;
    if (showCash) {
      if (manualBalance.trim().length > 0) {
        out["manual_balance_cents"] = dollarsToCents(manualBalance);
      }
      if (manualBalanceAsOf.length > 0) {
        out["manual_balance_as_of"] = manualBalanceAsOf;
      }
    }
    if (showLiability) {
      if (originatingPrincipal.trim().length > 0) {
        out["originating_principal_cents"] = dollarsToCents(originatingPrincipal);
      }
      if (currentPrincipal.trim().length > 0) {
        out["current_principal_cents"] = dollarsToCents(currentPrincipal);
      }
      if (interestRate.trim().length > 0) {
        out["interest_rate_pct"] = Number(interestRate);
      }
      if (payoffDate.length > 0) out["payoff_date"] = payoffDate;
      if (minimumPayment.trim().length > 0) {
        out["minimum_payment_cents"] = dollarsToCents(minimumPayment);
      }
      if (paymentDueDay.trim().length > 0) {
        out["payment_due_day_of_month"] = Number(paymentDueDay);
      }
    }
    if (showCreditCardExtras) {
      if (aprPct.trim().length > 0) out["apr_pct"] = Number(aprPct);
      if (creditLimit.trim().length > 0) {
        out["credit_limit_cents"] = dollarsToCents(creditLimit);
      }
      if (rewardsProgram.trim().length > 0) {
        out["rewards_program"] = rewardsProgram.trim();
      }
      if (annualFee.trim().length > 0) {
        out["annual_fee_cents"] = dollarsToCents(annualFee);
      }
    }
    if (showHsa) out["hsa_mode"] = hsaMode;
    if (showBeneficiary && beneficiaryMemberId.trim().length > 0) {
      out["beneficiary_member_id"] = beneficiaryMemberId.trim();
    }
    // Phase 5 Session 5 — plan_state_code + account_owner_member_id
    // surface for 529/UTMA. Empty strings stay omitted (additive optionals).
    if (showBeneficiary) {
      const stateCode = planStateCode.trim().toUpperCase();
      if (/^[A-Z]{2}$/.test(stateCode)) out["plan_state_code"] = stateCode;
      if (accountOwnerMemberId.trim().length > 0) {
        out["account_owner_member_id"] = accountOwnerMemberId.trim();
      }
    }
    if (showCrypto) {
      if (chain.trim().length > 0) out["chain"] = chain.trim();
      if (walletAddress.trim().length > 0) {
        out["wallet_address"] = walletAddress.trim();
      }
    }
    return out;
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    if (name.trim().length === 0 || institution.trim().length === 0) {
      setInlineError("Name and institution are required.");
      return;
    }
    mutation.mutate();
  }

  const heading = isEdit ? `Edit "${account?.name ?? ""}"` : "New account";

  return (
    <form
      onSubmit={onSubmit}
      aria-label={heading}
      className="flex flex-col gap-4 rounded-lg border border-zinc-200 p-4 dark:border-zinc-800"
    >
      <header className="flex items-center justify-between">
        <h3 className="text-base font-semibold">{heading}</h3>
        <button
          type="button"
          onClick={onClose}
          className="text-sm text-zinc-500 hover:underline"
          disabled={mutation.isPending}
        >
          Cancel
        </button>
      </header>

      {sourceLocked && (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          Imported from {account?.source}. Most fields are managed by re-import;
          you can still edit name, notes, beneficiary, ownership, and cost-basis
          method.
        </p>
      )}

      {/* Always-shown */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FieldText
          label="Name"
          required
          value={name}
          onChange={setName}
          disabled={mutation.isPending}
        />
        <FieldText
          label="Institution"
          required
          value={institution}
          onChange={setInstitution}
          disabled={mutation.isPending || sourceLocked}
        />
        <FieldSelect
          label="Type"
          value={accountType}
          onChange={(v) => setAccountType(v as AccountType)}
          disabled={mutation.isPending || sourceLocked}
          options={(Object.keys(ACCOUNT_TYPE_LABELS) as AccountType[]).map((t) => ({
            value: t,
            label: ACCOUNT_TYPE_LABELS[t],
          }))}
        />
        <FieldSelect
          label="Ownership"
          value={ownership}
          onChange={(v) => setOwnership(v as AccountOwnership)}
          disabled={mutation.isPending}
          options={OWNERSHIP_OPTIONS.map((o) => ({
            value: o.value,
            label: o.label,
          }))}
        />
      </div>

      {showInvestment && (
        <fieldset className="rounded-md border border-zinc-200 p-3 dark:border-zinc-800">
          <legend className="px-1 text-sm font-medium">Investment</legend>
          <FieldSelect
            label="Cost basis method"
            value={costBasisMethod}
            onChange={(v) => setCostBasisMethod(v as CostBasisMethod)}
            disabled={mutation.isPending}
            options={COST_BASIS_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
          />
          <p className="mt-1 text-xs text-zinc-500">
            Used at sale time. FIFO/LIFO/specific are informational until
            the sale handler ships in Session 3.3.
          </p>
        </fieldset>
      )}

      {showCash && (
        <fieldset className="rounded-md border border-zinc-200 p-3 dark:border-zinc-800">
          <legend className="px-1 text-sm font-medium">Cash balance</legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FieldMoney
              label="Manual balance ($)"
              value={manualBalance}
              onChange={setManualBalance}
              disabled={mutation.isPending || sourceLocked}
            />
            <FieldDate
              label="As of"
              value={manualBalanceAsOf}
              onChange={setManualBalanceAsOf}
              disabled={mutation.isPending || sourceLocked}
            />
          </div>
        </fieldset>
      )}

      {showLiability && (
        <fieldset className="rounded-md border border-zinc-200 p-3 dark:border-zinc-800">
          <legend className="px-1 text-sm font-medium">Loan terms</legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FieldMoney
              label="Originating principal ($)"
              value={originatingPrincipal}
              onChange={setOriginatingPrincipal}
              disabled={mutation.isPending || sourceLocked}
            />
            <FieldMoney
              label="Current principal ($)"
              value={currentPrincipal}
              onChange={setCurrentPrincipal}
              disabled={mutation.isPending || sourceLocked}
            />
            <FieldNumber
              label="Interest rate (%)"
              step="0.001"
              value={interestRate}
              onChange={setInterestRate}
              disabled={mutation.isPending || sourceLocked}
            />
            <FieldDate
              label="Payoff date"
              value={payoffDate}
              onChange={setPayoffDate}
              disabled={mutation.isPending || sourceLocked}
            />
            <FieldMoney
              label="Min payment ($)"
              value={minimumPayment}
              onChange={setMinimumPayment}
              disabled={mutation.isPending || sourceLocked}
            />
            <FieldNumber
              label="Payment due day (1–31)"
              step="1"
              value={paymentDueDay}
              onChange={setPaymentDueDay}
              disabled={mutation.isPending || sourceLocked}
            />
          </div>
        </fieldset>
      )}

      {showCreditCardExtras && (
        <fieldset className="rounded-md border border-zinc-200 p-3 dark:border-zinc-800">
          <legend className="px-1 text-sm font-medium">Credit card</legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FieldNumber
              label="APR (%)"
              step="0.01"
              value={aprPct}
              onChange={setAprPct}
              disabled={mutation.isPending || sourceLocked}
            />
            <FieldMoney
              label="Credit limit ($)"
              value={creditLimit}
              onChange={setCreditLimit}
              disabled={mutation.isPending || sourceLocked}
            />
            <FieldText
              label="Rewards program"
              value={rewardsProgram}
              onChange={setRewardsProgram}
              disabled={mutation.isPending || sourceLocked}
            />
            <FieldMoney
              label="Annual fee ($)"
              value={annualFee}
              onChange={setAnnualFee}
              disabled={mutation.isPending || sourceLocked}
            />
          </div>
        </fieldset>
      )}

      {showHsa && (
        <fieldset className="rounded-md border border-zinc-200 p-3 dark:border-zinc-800">
          <legend className="px-1 text-sm font-medium">HSA mode</legend>
          <FieldSelect
            label="Mode"
            value={hsaMode}
            onChange={(v) => setHsaMode(v as HsaMode)}
            disabled={mutation.isPending}
            options={HSA_MODE_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
          />
          <p className="mt-1 text-xs text-zinc-500">
            "Invested" mode also surfaces the cost-basis method picker.
            Receipts vault lands fully in Phase 7.
          </p>
        </fieldset>
      )}

      {showBeneficiary && (
        <fieldset className="rounded-md border border-zinc-200 p-3 dark:border-zinc-800">
          <legend className="px-1 text-sm font-medium">Beneficiary</legend>
          <BeneficiaryMemberSelect
            value={beneficiaryMemberId}
            onChange={setBeneficiaryMemberId}
            disabled={mutation.isPending}
          />
        </fieldset>
      )}

      {showBeneficiary && (
        <fieldset className="rounded-md border border-zinc-200 p-3 dark:border-zinc-800">
          <legend className="px-1 text-sm font-medium">
            {accountType === "utma"
              ? "UTMA custodian + governing state"
              : "529 owner + plan state"}
          </legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FieldText
              label="Plan / governing state (2-char ISO)"
              value={planStateCode}
              onChange={(v) => setPlanStateCode(v.toUpperCase())}
              disabled={mutation.isPending}
              placeholder="NY"
            />
            <BeneficiaryMemberSelect
              value={accountOwnerMemberId}
              onChange={setAccountOwnerMemberId}
              disabled={mutation.isPending}
              label={
                accountType === "utma"
                  ? "Custodian (controls account)"
                  : "Account owner-of-record (gets the deduction)"
              }
            />
          </div>
          {accountType === "529" || accountType === "529_prepaid" ? (
            <p className="mt-2 text-xs text-zinc-500">
              Set <strong>plan state = NY</strong> to track the NY $5K/$10K
              contribution deduction. Contributions to non-NY 529 plans don't
              get the NY deduction even if the owner files in NY.
            </p>
          ) : (
            <p className="mt-2 text-xs text-zinc-500">
              Governing state determines the age of majority (NY = 21,
              default = 18). Custodian controls the account until then.
            </p>
          )}
        </fieldset>
      )}

      {showCrypto && (
        <fieldset className="rounded-md border border-zinc-200 p-3 dark:border-zinc-800">
          <legend className="px-1 text-sm font-medium">Crypto</legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FieldText
              label="Chain"
              value={chain}
              onChange={setChain}
              list="chain-suggestions"
              disabled={mutation.isPending}
              placeholder="ethereum"
            />
            <datalist id="chain-suggestions">
              {CHAIN_SUGGESTIONS.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
            <FieldText
              label="Wallet address"
              value={walletAddress}
              onChange={setWalletAddress}
              disabled={mutation.isPending}
            />
          </div>
        </fieldset>
      )}

      <FieldTextarea
        label="Notes (optional)"
        value={notes}
        onChange={setNotes}
        disabled={mutation.isPending}
      />


      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? "Saving…" : isEdit ? "Save changes" : "Create"}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
          className="rounded-md border border-zinc-300 px-3 py-2 text-sm hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
        >
          Cancel
        </button>
      </div>

      {inlineError && (
        <ErrorMessage>{inlineError}</ErrorMessage>
      )}
    </form>
  );
}

// === Field primitives =================================================

const inputClass =
  "w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900 disabled:cursor-not-allowed disabled:opacity-60";

function FieldText({
  label,
  value,
  onChange,
  required,
  disabled,
  placeholder,
  list,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  list?: string;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium">
        {label}
        {required && <span className="text-rose-600"> *</span>}
      </label>
      <input
        id={id}
        type="text"
        className={inputClass}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        list={list}
      />
    </div>
  );
}

function FieldMoney({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium">
        {label}
      </label>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        className={inputClass}
        value={value}
        placeholder="0.00"
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  );
}

function FieldNumber({
  label,
  value,
  onChange,
  step,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  step?: string;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium">
        {label}
      </label>
      <input
        id={id}
        type="number"
        className={inputClass}
        value={value}
        step={step}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  );
}

function FieldSelect({
  label,
  value,
  onChange,
  disabled,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  options: ReadonlyArray<{ value: string; label: string }>;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium">
        {label}
      </label>
      <select
        id={id}
        className={inputClass}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function FieldTextarea({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium">
        {label}
      </label>
      <textarea
        id={id}
        className={inputClass}
        rows={2}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  );
}

function FieldDate({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium">
        {label}
      </label>
      <input
        id={id}
        type="date"
        className={inputClass}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  );
}

function centsOrEmpty(cents: number | undefined): string {
  if (cents === undefined || cents === null) return "";
  return centsToDollars(cents);
}

/**
 * Beneficiary picker — Phase 5 Session 1. Loads family_members + maps
 * to a `<select>`. Active members come first; archived members render
 * with an `(archived)` suffix so a previously-set beneficiary still
 * resolves correctly even after the member is archived.
 */
function BeneficiaryMemberSelect({
  value,
  onChange,
  disabled,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  /** Override default "Beneficiary" label (Phase 5.5: also used for 529 owner / UTMA custodian). */
  label?: string;
}) {
  const id = useId();
  const { data, isLoading } = useQuery({
    queryKey: ["family-members", { includeArchived: true }],
    queryFn: () => listFamilyMembers(true),
  });
  const members = data?.family_members ?? [];

  // Sort: active first, then archived; alphabetical within each group.
  const sorted = useMemo(
    () =>
      [...members].sort((a, b) => {
        if (a.is_archived !== b.is_archived) {
          return a.is_archived ? 1 : -1;
        }
        return a.name.localeCompare(b.name);
      }),
    [members],
  );

  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium">
        {label ?? "Beneficiary"}
      </label>
      <select
        id={id}
        className={inputClass}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || isLoading}
      >
        <option value="">— None —</option>
        {sorted.map((m) => (
          <option key={m.member_id} value={m.member_id}>
            {m.name}
            {m.is_archived ? " (archived)" : ""}
          </option>
        ))}
      </select>
      {!isLoading && members.length === 0 && (
        <p className="mt-1 text-xs text-zinc-500">
          No family members yet — add some at <a href="/family" className="underline">Family</a>.
        </p>
      )}
    </div>
  );
}
